import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthCtx = createContext({ user: null, loading: true, login: async () => {}, logout: () => {} });

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const check = useCallback(async () => {
        const token = localStorage.getItem("tada_admin_token");
        if (!token) { setUser(null); setLoading(false); return; }
        try {
            const { data } = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUser({ ...data, token });
            if (data.tenant_slug) localStorage.setItem("tada_tenant_slug", data.tenant_slug);
            else localStorage.removeItem("tada_tenant_slug");
            window.dispatchEvent(new Event("tenantslugchange"));
        } catch {
            localStorage.removeItem("tada_admin_token");
            localStorage.removeItem("tada_tenant_slug");
            setUser(null);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { check(); }, [check]);

    const login = async (username, password) => {
        const { data } = await axios.post(`${API}/auth/login`, { username, password });
        localStorage.setItem("tada_admin_token", data.token);
        if (data.tenant_slug) localStorage.setItem("tada_tenant_slug", data.tenant_slug);
        else localStorage.removeItem("tada_tenant_slug");
        window.dispatchEvent(new Event("tenantslugchange"));
        setUser({
            username: data.username,
            role: data.role,
            tenant_id: data.tenant_id,
            tenant_slug: data.tenant_slug,
            token: data.token,
        });
        return data;
    };

    const logout = () => {
        localStorage.removeItem("tada_admin_token");
        localStorage.removeItem("tada_tenant_slug");
        window.dispatchEvent(new Event("tenantslugchange"));
        setUser(null);
    };

    // Impersonate a borgo — used by super admin
    const impersonate = async (tenantId) => {
        const token = localStorage.getItem("tada_admin_token");
        const { data } = await axios.post(`${API}/admin/tenants/${tenantId}/impersonate`, {}, {
            headers: { Authorization: `Bearer ${token}` },
        });
        localStorage.setItem("tada_admin_token", data.token);
        if (data.tenant_slug) localStorage.setItem("tada_tenant_slug", data.tenant_slug);
        window.dispatchEvent(new Event("tenantslugchange"));
        setUser({
            username: data.username, role: data.role,
            tenant_id: data.tenant_id, tenant_slug: data.tenant_slug,
            token: data.token,
        });
        return data;
    };

    return <AuthCtx.Provider value={{ user, loading, login, logout, impersonate }}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);

export const adminAxios = () => {
    const token = localStorage.getItem("tada_admin_token");
    return axios.create({
        baseURL: API,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Normalize a caught error (axios / plain / Pydantic 422) into a *string*
 * that can be safely rendered as a React child. Pydantic 422 returns
 * detail as an array of {type,loc,msg,input,url} objects that would crash React.
 */
export const errText = (e, fallback = "Errore") => {
    try {
        const detail = e?.response?.data?.detail ?? e?.response?.data ?? e?.message ?? e;
        if (!detail) return fallback;
        if (typeof detail === "string") return detail;
        if (Array.isArray(detail)) {
            return detail
                .map((d) => {
                    if (typeof d === "string") return d;
                    const loc = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
                    const msg = d?.msg || d?.message || JSON.stringify(d);
                    return loc ? `${loc}: ${msg}` : msg;
                })
                .filter(Boolean)
                .join(" · ");
        }
        if (typeof detail === "object") {
            return detail?.msg || detail?.message || JSON.stringify(detail);
        }
        return String(detail);
    } catch (_) {
        return fallback;
    }
};
